import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import {
  Mutation,
  MutationAssignToAccessGrantsArgs,
  MutationUnassignFromAccessGrantsArgs,
  Query,
  QueryGetVisitorGroupsForTenantArgs
} from 'graphql/generated/graphqlTypes';
import { ASSIGN_TO_ACCESS_GRANTS, UNASSIGN_TO_ACCESS_GRANTS } from 'graphql/mutations';
import { GET_VISITOR_GROUPS_FOR_TENANT_WITH_ACCESS_GRANTS } from 'graphql/queries';
import { filterGroupsForUser } from 'hooks/shared/filterGroupsForUser';
import { useEffect } from 'react';
import { setVisitorGroupsWithAccessGrantsAction, updateToAccessGrantsAction } from 'state/actions/accessGrants';
import { authVar, visitorsGroupsWithAccessGrantsVar } from 'state/vars';
import { BaseHookProps } from '../shared/types';

const useAccessGrants = ({ handleFetchError }: BaseHookProps) => {
  const isAdditionalLogsEnabled = process.env.REACT_APP_IS_ADDITIONAL_LOGS_ENABLED === 'true';
  const { user } = useReactiveVar(authVar);
  const visitorsGroupsWithAccessGrants = useReactiveVar(visitorsGroupsWithAccessGrantsVar);

  const [getVisitorsGroupsMembers, { loading }] = useLazyQuery<
    Pick<Query, 'getVisitorGroupsForTenant'>,
    QueryGetVisitorGroupsForTenantArgs
  >(GET_VISITOR_GROUPS_FOR_TENANT_WITH_ACCESS_GRANTS, {
    onCompleted: (data) => {
      const groups = filterGroupsForUser(data.getVisitorGroupsForTenant, user);
      if (isAdditionalLogsEnabled) console.log(groups);
      setVisitorGroupsWithAccessGrantsAction(groups);
    },
    onError: (err) => {
      if (isAdditionalLogsEnabled) console.log(err);
      handleFetchError('Error while fetching visitor groups');
    },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    getVisitorsGroupsMembers();
  }, [getVisitorsGroupsMembers]);

  const [assignToAccessGrantsMutation, { loading: assignToAccessGrantsMutationLoading }] = useMutation<
    Pick<Mutation, 'assignToAccessGrants'>,
    MutationAssignToAccessGrantsArgs
  >(ASSIGN_TO_ACCESS_GRANTS, {
    onCompleted: (data) => {
      data.assignToAccessGrants.forEach((grant) => updateToAccessGrantsAction(grant));
    },
    onError: () => {
      handleFetchError('Error while adding access');
    }
  });

  const [unassignFromAccessGrantsMutation, { loading: unassignFromAccessGrantsMutationLoading }] = useMutation<
    Pick<Mutation, 'unassignFromAccessGrants'>,
    MutationUnassignFromAccessGrantsArgs
  >(UNASSIGN_TO_ACCESS_GRANTS, {
    onCompleted: (data) => {
      data.unassignFromAccessGrants.forEach((grant) => updateToAccessGrantsAction(grant));
    },
    onError: () => {
      handleFetchError('Error while deleting access');
    }
  });

  const assignToAccessGrants = async (accessGrantIds: string[], doorIds: string[], locationIds: string[]) => {
    const { data } = await assignToAccessGrantsMutation({
      variables: {
        accessGrantIds,
        doorIds,
        locationIds
      }
    });
    return data;
  };

  const unassignFromAccessGrants = async (accessGrantIds: string[], doorIds: string[], locationIds: string[]) => {
    const { data } = await unassignFromAccessGrantsMutation({
      variables: {
        accessGrantIds,
        doorIds,
        locationIds
      }
    });
    return data;
  };

  return {
    visitorsGroupsWithAccessGrants,
    loading,
    assignToAccessGrants,
    assignToAccessGrantsMutationLoading,
    unassignFromAccessGrants,
    unassignFromAccessGrantsMutationLoading
  };
};

export default useAccessGrants;
